import { Grid } from './Grid';
import { Layer } from './Layer';
import { Color, ColorMode, Rect, TRANSPARENT } from './types';
import type { VariableDef, VariableValues } from '../runtime/Variables';

export interface Guide {
  axis: 'x' | 'y';
  position: number;
}

/** Project-wide named values (colours, characters, spacings). */
export interface DesignTokens {
  colors: Record<string, string>;
  characters: Record<string, string>;
  numbers: Record<string, number>;
}

export interface DocumentOptions {
  name?: string;
  width?: number;
  height?: number;
  colorMode?: ColorMode;
  background?: Color;
}

export function emptyTokens(): DesignTokens {
  return { colors: {}, characters: {}, numbers: {} };
}

/**
 * The editable project: a stack of equally sized layers plus everything that
 * is not pixels (variables, tokens, guides, preview data).
 *
 * `revision` increments on every mutation; the renderer and React store use it
 * as a cheap change token instead of deep-comparing grids.
 */
export class TuiDocument {
  name: string;
  width: number;
  height: number;
  colorMode: ColorMode;
  /** Canvas background, used for export and preview. */
  background: Color;
  layers: Layer[] = [];
  activeLayerId = '';
  variables: VariableDef[] = [];
  previewValues: VariableValues = {};
  tokens: DesignTokens = emptyTokens();
  guides: Guide[] = [];
  revision = 0;

  private compositeCache: Grid | null = null;
  private compositeRevision = -1;

  constructor(opts: DocumentOptions = {}) {
    this.name = opts.name ?? 'Untitled';
    this.width = Math.max(1, opts.width ?? 80);
    this.height = Math.max(1, opts.height ?? 24);
    this.colorMode = opts.colorMode ?? 'truecolor';
    this.background = opts.background ?? 0x06070a;
    const base = new Layer(this.width, this.height, { name: 'Background' });
    this.layers.push(base);
    this.activeLayerId = base.id;
  }

  touch(): void {
    this.revision += 1;
  }

  get activeLayer(): Layer {
    return this.layers.find((l) => l.id === this.activeLayerId) ?? this.layers[0];
  }

  layerById(id: string): Layer | undefined {
    return this.layers.find((l) => l.id === id);
  }

  layerIndex(id: string): number {
    return this.layers.findIndex((l) => l.id === id);
  }

  addLayer(name?: string, atIndex?: number): Layer {
    const layer = new Layer(this.width, this.height, {
      name: name ?? `Layer ${this.layers.length + 1}`,
    });
    const i = atIndex ?? this.layers.length;
    this.layers.splice(Math.max(0, Math.min(this.layers.length, i)), 0, layer);
    this.activeLayerId = layer.id;
    this.touch();
    return layer;
  }

  insertLayer(layer: Layer, index: number): void {
    this.layers.splice(Math.max(0, Math.min(this.layers.length, index)), 0, layer);
    this.touch();
  }

  removeLayer(id: string): Layer | null {
    if (this.layers.length <= 1) return null;
    const i = this.layerIndex(id);
    if (i < 0) return null;
    const [removed] = this.layers.splice(i, 1);
    if (this.activeLayerId === id) {
      this.activeLayerId = this.layers[Math.min(i, this.layers.length - 1)].id;
    }
    this.touch();
    return removed;
  }

  moveLayer(id: string, toIndex: number): void {
    const from = this.layerIndex(id);
    if (from < 0) return;
    const to = Math.max(0, Math.min(this.layers.length - 1, toIndex));
    if (from === to) return;
    const [layer] = this.layers.splice(from, 1);
    this.layers.splice(to, 0, layer);
    this.touch();
  }

  duplicateLayer(id: string): Layer | null {
    const src = this.layerById(id);
    if (!src) return null;
    const copy = src.clone(true);
    copy.name = `${src.name} copy`;
    this.layers.splice(this.layerIndex(id) + 1, 0, copy);
    this.activeLayerId = copy.id;
    this.touch();
    return copy;
  }

  resize(width: number, height: number, offsetX = 0, offsetY = 0): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.layers.forEach((l) => l.resize(this.width, this.height, offsetX, offsetY));
    this.compositeCache = null;
    this.touch();
  }

  /** Flattened view of all visible layers, cached per revision. */
  composite(): Grid {
    if (this.compositeCache && this.compositeRevision === this.revision) return this.compositeCache;
    const out = new Grid(this.width, this.height);
    for (const layer of this.layers) {
      if (!layer.visible) continue;
      out.composite(layer.grid);
    }
    this.compositeCache = out;
    this.compositeRevision = this.revision;
    return out;
  }

  /** Composite of a subset of layers (used by the layer-isolation preview). */
  compositeOf(layerIds: readonly string[]): Grid {
    const out = new Grid(this.width, this.height);
    for (const layer of this.layers) {
      if (!layerIds.includes(layer.id)) continue;
      out.composite(layer.grid);
    }
    return out;
  }

  /** Bounds of painted content across all visible layers. */
  contentBounds(): Rect | null {
    return this.composite().contentBounds();
  }

  clone(): TuiDocument {
    const d = new TuiDocument({
      name: this.name,
      width: this.width,
      height: this.height,
      colorMode: this.colorMode,
      background: this.background,
    });
    d.layers = this.layers.map((l) => l.clone());
    d.activeLayerId = this.activeLayerId;
    d.variables = this.variables.map((v) => ({ ...v }));
    d.previewValues = { ...this.previewValues };
    d.tokens = {
      colors: { ...this.tokens.colors },
      characters: { ...this.tokens.characters },
      numbers: { ...this.tokens.numbers },
    };
    d.guides = this.guides.map((g) => ({ ...g }));
    return d;
  }

  /** Resolves a token reference like "colors.primary"; passthrough otherwise. */
  resolveColorToken(value: string): Color {
    if (value.startsWith('colors.')) {
      const hex = this.tokens.colors[value.slice('colors.'.length)];
      if (hex) return parseInt(hex.replace('#', ''), 16) & 0xffffff;
      return TRANSPARENT;
    }
    return parseInt(value.replace('#', ''), 16) & 0xffffff;
  }
}
